import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { gettext } from '@eventbrite/i18n';

import { Button, SIZE_BLOCK, STYLE_NEUTRAL } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import { LogoGoogle } from '@eventbrite/eds-iconography';

import {
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
    DATA_HEAP_ID_LOGIN_WITH_GOOGLE_BUTTON,
} from './constants';
import { injectScriptTag } from './utils';

import './GoogleLoginButton.scss';
import './SocialLoginButton.scss';

export default class GoogleLoginButton extends Component {
    static propTypes = {
        onSocialConnectVerified: PropTypes.func,
        onSocialConnectNotAuthorized: PropTypes.func,
        onLoadComplete: PropTypes.func,
        onClick: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.state = {
            googleProfile: false,
        };
    }

    componentDidMount() {
        const { asyncStrategy, skipScriptLoading } = this.props;

        if (!skipScriptLoading) {
            injectScriptTag({
                doc: document,
                id: 'google-id-auth',
                src: 'https://apis.google.com/js/platform.js',
                asyncStrategy,
                onLoad: this.googleInit,
            });
        }
    }

    /* istanbul ignore next */
    googleInit = () => {
        const { clientId, scope, skipScriptLoading } = this.props;
        const currentComponent = this;
        /*eslint-disable no-undef*/
        if (window.gapi && !skipScriptLoading) {
            gapi?.load?.('auth2', function () {
                gapi?.auth2
                    ?.init?.({
                        client_id: clientId,
                        scope: scope,
                    })
                    .then(() => {
                        const auth2 =
                            gapi.auth2 && gapi.auth2.getAuthInstance();
                        if (auth2?.isSignedIn?.get?.()) {
                            var profile = auth2?.currentUser
                                ?.get()
                                ?.getBasicProfile?.();
                            if (profile) {
                                currentComponent.setState({
                                    googleProfile: profile,
                                });
                            }
                        }
                    });
            });
        }
    };

    // signOut = () => {
    //     /*eslint-disable no-undef*/
    //     if (gapi) {
    //         const auth2 = gapi.auth2 && gapi.auth2.getAuthInstance();

    //         auth2.signOut().then(function () {
    //             console.log('User signed out.');
    //         });
    //     }
    // };

    handleOnClick = () => {
        const {
            onSocialConnectVerified,
            onSocialConnectNotAuthorized,
            onSocialLogInUserAuthorized,
            onClick,
        } = this.props;
        onClick?.(SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE);

        /* istanbul ignore next */
        /*eslint-disable no-undef*/
        if (window.gapi) {
            const auth2 = gapi.auth2 && gapi.auth2.getAuthInstance();
            gapi.load('auth2', function () {
                auth2.signIn().then(
                    (googleUser) => {
                        if (!isEmpty(googleUser)) {
                            if (auth2.isSignedIn.get() == true) {
                                onSocialLogInUserAuthorized?.(
                                    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
                                    googleUser,
                                );
                                onSocialConnectVerified?.(
                                    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
                                    {
                                        accessToken:
                                            googleUser.getAuthResponse()
                                                .id_token,
                                        email: googleUser
                                            .getBasicProfile()
                                            .getEmail(),
                                        firstName: googleUser
                                            .getBasicProfile()
                                            .getGivenName(),
                                        lastName: googleUser
                                            .getBasicProfile()
                                            .getFamilyName(),
                                        picture: googleUser
                                            .getBasicProfile()
                                            .getImageUrl(),
                                        userID: googleUser
                                            .getBasicProfile()
                                            .getId(),
                                        sourceProvider:
                                            SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
                                    },
                                );
                            } else {
                                onSocialConnectNotAuthorized?.(
                                    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
                                    {
                                        sourceProvider:
                                            SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
                                        status: googleUser,
                                    },
                                );
                            }
                        }
                    },
                    function (status) {
                        onSocialConnectNotAuthorized?.(
                            SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
                            {
                                sourceProvider:
                                    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
                                status,
                            },
                        );
                    },
                );
            });
        }
    };

    render() {
        const { buttonGoogleText } = this.props;
        const { googleProfile } = this.state;
        const buttonText = buttonGoogleText || gettext('Continue with Google');
        const dataAutomation = 'google-button-login';
        if (googleProfile?.getId?.()) {
            return (
                <div className="google-button-container">
                    <Button
                        data-automation={dataAutomation}
                        data-heap-id={DATA_HEAP_ID_LOGIN_WITH_GOOGLE_BUTTON}
                        type="button"
                        size={SIZE_BLOCK}
                        onClick={this.handleOnClick}
                        style={STYLE_NEUTRAL}
                    >
                        <div className="google-button__content eds-align--center">
                            <div className="google-button__logo eds-g-cell">
                                <div className="eds-align--left">
                                    <LogoGoogle />
                                </div>
                            </div>
                            <div className="google-button__text eds-g-cell">
                                {gettext('Continue as %(firstName)s ', {
                                    firstName: googleProfile?.getGivenName?.(),
                                })}
                            </div>
                            <div className="eds-g-cell google-button__image-wrapper">
                                <div className="eds-align--center">
                                    <img
                                        className="google-button__image"
                                        src={googleProfile?.getImageUrl?.()}
                                        alt={googleProfile?.getImageUrl?.()}
                                    />
                                </div>
                            </div>
                        </div>
                    </Button>
                </div>
            );
        } else {
            return (
                <div className="social-login__container">
                    <Button
                        type="button"
                        size={SIZE_BLOCK}
                        onClick={this.handleOnClick}
                        style={STYLE_NEUTRAL}
                        data-automation={dataAutomation}
                        data-heap-id={DATA_HEAP_ID_LOGIN_WITH_GOOGLE_BUTTON}
                    >
                        <div className="google-login__icon">
                            <Icon type={<LogoGoogle />} size="small" />
                        </div>
                        {buttonText}
                    </Button>
                </div>
            );
        }
    }
}
